import { useEffect, useMemo, useState } from 'react'
import { Check } from 'lucide-react'
import { Button } from '@deepguide-ai/ui/button'
import { cn } from '@/lib/utils'

interface ToolPartProps {
  part: any
}

interface AskQuestionDef {
  id: string
  question: string
  options: string[]
  allowOther: boolean
  paramName: string | null
}

interface AskQuestionsPayload {
  intro: string | null
  questions: AskQuestionDef[]
}

type AnswerMap = Record<
  string,
  { selectedOption: string | null; customText: string | null }
>

interface AnsweredPair {
  question: string
  answer: string
}

function getToolPayload(part: any): Record<string, unknown> | null {
  const candidate = part.input ?? part.args ?? part.output ?? part.result
  return candidate && typeof candidate === 'object' ? candidate : null
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

function extractAskQuestionsPayload(part: any): AskQuestionsPayload | null {
  const candidate = getToolPayload(part)
  if (!candidate) return null
  const rawQuestions = candidate.questions
  if (!Array.isArray(rawQuestions) || rawQuestions.length === 0) return null
  const questions: AskQuestionDef[] = []
  for (const entry of rawQuestions) {
    if (!entry || typeof entry !== 'object') continue
    const q = entry as Record<string, unknown>
    if (typeof q.id !== 'string' || typeof q.question !== 'string') continue
    const options = Array.isArray(q.options)
      ? q.options.filter((v): v is string => typeof v === 'string')
      : []
    if (options.length === 0) continue
    questions.push({
      id: q.id,
      question: q.question,
      options,
      allowOther: q.allowOther !== false,
      paramName: typeof q.paramName === 'string' ? q.paramName : null,
    })
  }
  if (questions.length === 0) return null
  return {
    intro: typeof candidate.intro === 'string' ? candidate.intro : null,
    questions,
  }
}

// Reads the persisted tool output ({ ok: true, answers: [...] }) so an answered
// card still shows its Q&A after reload, when local `submitted` state is gone.
function extractAnsweredPairsFromOutput(part: any): AnsweredPair[] | null {
  const rawOutput = part?.output ?? part?.result
  const output =
    typeof rawOutput === 'string' ? safeJsonParse(rawOutput) : rawOutput
  if (!output || typeof output !== 'object') return null
  const answers = (output as Record<string, unknown>).answers
  if (!Array.isArray(answers) || answers.length === 0) return null
  const pairs: AnsweredPair[] = []
  for (const entry of answers) {
    if (!entry || typeof entry !== 'object') continue
    const a = entry as Record<string, unknown>
    if (typeof a.question !== 'string') continue
    const answer =
      a.answer === null || a.answer === undefined ? '' : String(a.answer)
    pairs.push({ question: a.question, answer })
  }
  return pairs.length > 0 ? pairs : null
}

const OTHER_OPTION_KEY = '__other__'

export function AskQuestionsMessage({ part }: ToolPartProps) {
  const payload = extractAskQuestionsPayload(part)
  const toolCallId = part.toolCallId ?? part.toolInvocation?.toolCallId
  const [submitted, setSubmitted] = useState<AnswerMap | null>(null)
  const payloadKey = payload?.questions.map((q) => q.id).join('|') ?? ''
  const initial = useMemo(() => {
    const out: AnswerMap = {}
    for (const q of payload?.questions ?? []) {
      out[q.id] = { selectedOption: null, customText: null }
    }
    return out
  }, [payloadKey])
  const [draft, setDraft] = useState<AnswerMap>(initial)

  useEffect(() => {
    setDraft(initial)
  }, [initial])

  if (!payload) return null

  const localPairs: AnsweredPair[] | null = submitted
    ? payload.questions.map((q) => {
        const answer = submitted[q.id]
        const value =
          answer?.selectedOption === OTHER_OPTION_KEY
            ? answer.customText?.trim()
            : answer?.selectedOption
        return { question: q.question, answer: value ?? '' }
      })
    : null
  const answeredPairs = localPairs ?? extractAnsweredPairsFromOutput(part)

  if (answeredPairs) {
    return (
      <div className='space-y-2.5 rounded-md border border-border bg-card px-3 py-3'>
        <div className='flex items-center gap-1.5 text-[12px] font-semibold text-status-green-strong'>
          <Check className='h-3.5 w-3.5' />
          Answered
        </div>
        <dl className='space-y-2'>
          {answeredPairs.map((pair, index) => (
            <div key={index} className='space-y-0.5'>
              <dt className='text-[12px] text-text-subtle'>{pair.question}</dt>
              <dd className='text-[13px] font-medium text-foreground'>
                {pair.answer || '—'}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    )
  }

  const complete = payload.questions.every((q) => {
    const answer = draft[q.id]
    if (!answer?.selectedOption) return false
    if (answer.selectedOption === OTHER_OPTION_KEY) {
      return Boolean(answer.customText?.trim())
    }
    return true
  })

  const submit = () => {
    if (!complete || !toolCallId) return
    setSubmitted(draft)
    const answers = payload.questions.map((q) => {
      const answer = draft[q.id]
      const value =
        answer?.selectedOption === OTHER_OPTION_KEY
          ? answer.customText?.trim()
          : answer?.selectedOption
      return {
        id: q.id,
        question: q.question,
        paramName: q.paramName,
        answer: value ?? null,
      }
    })
    window.dispatchEvent(
      new CustomEvent('screenata:tool-output', {
        detail: {
          scope: 'screenata-chat',
          tool: 'askQuestions',
          toolCallId,
          output: { ok: true, answers },
        },
      })
    )
  }

  return (
    <div className='space-y-3 rounded-md border border-border bg-card px-3 py-3'>
      {payload.intro && <p className='text-[12px] text-text-body'>{payload.intro}</p>}
      {payload.questions.map((q) => {
        const selected = draft[q.id]?.selectedOption
        return (
          <fieldset key={q.id} className='space-y-1.5'>
            <legend className='text-[13px] font-medium text-foreground'>{q.question}</legend>
            {q.options.map((option) => (
              <label
                key={option}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-[12px]',
                  selected === option
                    ? 'border-ring bg-selected'
                    : 'border-border bg-card hover:bg-surface-subtle'
                )}
              >
                <input
                  type='radio'
                  name={`q-${q.id}`}
                  checked={selected === option}
                  onChange={() =>
                    setDraft((current) => ({
                      ...current,
                      [q.id]: { selectedOption: option, customText: null },
                    }))
                  }
                  className='h-3 w-3 accent-cobalt-solid'
                />
                <span>{option}</span>
              </label>
            ))}
            {q.allowOther && (
              <input
                type='text'
                placeholder='Other answer'
                value={draft[q.id]?.customText ?? ''}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    [q.id]: {
                      selectedOption: OTHER_OPTION_KEY,
                      customText: event.target.value,
                    },
                  }))
                }
                className='h-8 w-full rounded-md border border-border-divider px-2 text-[12px]'
              />
            )}
          </fieldset>
        )
      })}
      <div className='flex justify-end'>
        <Button
          size='sm'
          className='h-7 bg-primary px-3 text-[12px] hover:bg-primary/90'
          disabled={!complete || !toolCallId}
          onClick={submit}
        >
          Submit
        </Button>
      </div>
    </div>
  )
}
